'use client';

import * as React from 'react';
import Link from 'next/link';
import { cn } from '@workspace/ui/lib/utils';
import { routes } from '@workspace/routes';
import {
  Wand2,
  Sparkles,
  Music,
  Share2,
  Zap,
  Palette,
  ArrowRightIcon,
  TrendingUp
} from 'lucide-react';
import { ComingSoonModal } from '../coming-soon-modal';

export function Features(): React.JSX.Element {
  const [showComingSoon, setShowComingSoon] = React.useState(false);
  const features = [
    {
      name: 'Just Type Your Video Idea',
      className: 'col-span-12 md:col-span-8',
      Icon: Sparkles,
      iconBg: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      iconColor: 'text-[#00C6FF]',
      borderColor: 'border-[#4531FF]/20',
      description: 'Write any concept in plain language - music video, commercial, or film. AI understands your vision and creates it with perfect cinematography.',
      stat: 'Any idea becomes video',
      benefits: ['Natural storytelling', 'Character creation', 'Scene composition'],
      href: '#interactive-demo',
    },
    {
      name: 'Instant Professional Results',
      className: 'col-span-12 md:col-span-4',
      Icon: Wand2,
      iconBg: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      iconColor: 'text-[#00C6FF]',
      borderColor: 'border-[#4531FF]/20',
      description: 'No editing skills needed. AI handles cinematography, cuts, transitions, and pacing like a pro director.',
      stat: 'Zero experience required',
      benefits: ['Auto cinematography', 'Perfect pacing'],
      href: '#how-it-works',
    },
    {
      name: 'Cinema-Quality Output',
      className: 'col-span-12 md:col-span-4',
      Icon: Palette,
      iconBg: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      iconColor: 'text-[#00C6FF]',
      borderColor: 'border-[#4531FF]/20',
      description: 'Get Hollywood-level visuals, lighting, and effects. Your videos look professionally produced.',
      stat: 'Studio-grade quality',
      benefits: ['Cinematic lighting', 'Pro visual effects'],
      href: '#features-tabs',
    },
    {
      name: 'Original Music & Sound',
      className: 'col-span-12 md:col-span-4',
      Icon: Music,
      iconBg: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      iconColor: 'text-[#00C6FF]',
      borderColor: 'border-[#4531FF]/20',
      description: 'AI composes original soundtracks that match your story\'s emotion. From epic scores to subtle ambience.',
      stat: 'Realistic audio',
      benefits: ['Original music', 'Perfect mood matching'],
      href: '#interactive-demo',
    },
    {
      name: 'Share Your Creation',
      className: 'col-span-12 md:col-span-4',
      Icon: Share2,
      iconBg: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      iconColor: 'text-[#00C6FF]',
      borderColor: 'border-[#4531FF]/20',
      description: 'Export in any format. Share on social media, use for marketing, or build your video portfolio.',
      stat: 'Universal compatibility',
      benefits: ['Multiple formats', 'Easy sharing'],
      href: '#interactive-demo',
    },
  ];

  const [hoveredFeature, setHoveredFeature] = React.useState<number | null>(null);

  return (
    <section id="features" className="py-24 lg:py-32 relative bg-[#0E0F1A]">
      <div className="container px-6">
        <div className="mx-auto max-w-7xl">

          {/* Section Header - Enhanced Typography */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-full px-5 py-2.5 mb-8 shadow-sm border border-white/20">
              <TrendingUp className="w-4 h-4 text-[#00C6FF]" />
              <span className="font-semibold text-white text-sm tracking-wide">CORE FEATURES</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              Everything you need to create
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#4531FF] to-[#00C6FF]">
                professional videos
              </span>
            </h2>

            <p className="text-lg lg:text-xl text-[#9EA1AE] max-w-3xl mx-auto leading-relaxed">
              From music videos to commercials to films—anyone can create cinema-quality content.
            </p>
          </div>

          {/* Feature Cards Grid - Improved Layout */}
          <div className="grid grid-cols-12 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.name}
                className={cn("group relative", feature.className)}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={cn(
                  "bg-[#1A1B29]/80 backdrop-blur-xl rounded-3xl p-8 h-full relative overflow-hidden border transition-all duration-500 hover:shadow-xl hover:-translate-y-2",
                  "border-[#4531FF]/25",
                  hoveredFeature === index ? 'shadow-2xl scale-[1.02]' : 'shadow-lg'
                )}>

                  {/* Sophisticated Icon Treatment */}
                  <div className={cn(
                    "w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110",
                    feature.iconBg
                  )}>
                    <feature.Icon className={cn("h-8 w-8", feature.iconColor)} />
                  </div>

                  {/* Enhanced Typography */}
                  <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-4 leading-snug">
                    {feature.name}
                  </h3>

                  <p className="text-[#9EA1AE] mb-6 leading-relaxed text-base">
                    {feature.description}
                  </p>

                  {/* Benefits List - New Addition */}
                  <div className="mb-6">
                    <ul className="space-y-2">
                      {feature.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-[#9EA1AE]">
                          <div className="w-1.5 h-1.5 bg-[#00C6FF] rounded-full" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Enhanced Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <div className="text-sm font-semibold text-[#00C6FF] mb-1">
                        {feature.stat}
                      </div>
                    </div>
                    <a
                      href={feature.href}
                      className="w-10 h-10 bg-white/10 hover:bg-[#4531FF]/20 rounded-xl flex items-center justify-center transition-colors group/arrow"
                    >
                      <ArrowRightIcon className="h-5 w-5 text-[#9EA1AE] group-hover/arrow:text-[#00C6FF]" />
                    </a>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#4531FF]/5 via-transparent to-[#00C6FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Bottom CTA */}
          <div className="text-center mt-20">
            <div className="bg-[#1A1B29]/80 backdrop-blur-xl rounded-3xl p-12 border border-[#4531FF]/20 shadow-lg">
              <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-4 leading-snug">
                Ready to create your first video?
              </h3>
              <p className="text-[#9EA1AE] mb-8 text-base leading-relaxed max-w-2xl mx-auto">
                Join thousands creating music videos, commercials, and cinematic content.
              </p>
              <button
                onClick={() => setShowComingSoon(true)}
                className="bg-gradient-to-r from-[#4531FF] to-[#00C6FF] text-white text-lg font-semibold px-10 py-4 rounded-full hover:shadow-lg transition-all duration-200 hover:scale-105 hover:from-[#3420E5] hover:to-[#00A3D9] inline-flex items-center gap-2 hover:-translate-y-0.5 hover:brightness-110 group shadow-lg"
              >
                Create Your First Video
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Coming Soon Modal */}
      <ComingSoonModal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} />
    </section>
  );
}